import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { forkJoin, map, Observable } from 'rxjs';
import { JsFndaUser, JsMenu } from 'src/app/layout/models/layout-model';
import { URLConst } from '../const/url.const';
import { EnvironmentconfigService } from '../service/environmentconfig.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly redirectUrlStorageKey = 'redirectUrl';

    userPermission = [
        { apid: '*', c: false, r: false, u: false, d: false }
    ];

    AllSiteList;
    AllVendorList;
    AllBuyerList;
    AllDeptList;
    defautSite;
    StatementMaintainData;
    StatementMaintainDataTotal;
    StatementAbnormalData;
    StatementAbnormalDataTotal;

    constructor(
        private httpClient: HttpClient,
        private msal: MsalService,
        private envServ: EnvironmentconfigService
    ) {
    }

    getCurrentUserInfo(): JsFndaUser {
        //先取switched用户信息,取不到再取loginUser
        const switchedToken = localStorage.getItem('switchedUserToken');
        if (switchedToken) {
            return JSON.parse(localStorage.getItem('switchedUser')!);
        } else {
            return JSON.parse(localStorage.getItem('loginUser')!);
        }
    }
    getADLoginUser(): JsFndaUser {
        return JSON.parse(localStorage.getItem('loginUser'));
    }
    getSwitchedToken(): string | null {
        return localStorage.getItem('switchedUserToken') || null;
    }
    setSwitched(token: string, user: JsFndaUser | undefined) {
        localStorage.setItem('switchedUserToken', token);
        localStorage.setItem('switchedUser', JSON.stringify(user || {}));
    }
    clearSwitched() {
        localStorage.removeItem('switchedUserToken');
        localStorage.removeItem('switchedUser');
    }
    clearLoginUser() {
        localStorage.removeItem('loginUser');
    }
    setRedirectUrl(url: string) {
        sessionStorage.setItem(this.redirectUrlStorageKey, url);
    }
    getRedirectUrl(): string | null {
        return sessionStorage.getItem(this.redirectUrlStorageKey);
    }
    clearRedirectUrl() {
        sessionStorage.removeItem(this.redirectUrlStorageKey);
    }
    consumeRedirectUrl(): string | null {
        const redirectUrl = this.getRedirectUrl();
        this.clearRedirectUrl();
        return redirectUrl;
    }
    setLoginUser(user?: JsFndaUser) {
        localStorage.setItem('loginUser', JSON.stringify(user || {}));
    }
    async login() {
        const request: RedirectRequest = {
            scopes: this.envServ.authConfig.entraID.scopes
        };
        try {
            //強制清除前一次未處理完的重定向，避免登入異常
            await this.msal.handleRedirectObservable().toPromise().catch(err => { console.error('Redirect error:', err); });
            return await this.msal.loginRedirect(request).toPromise();
        } catch (error) {
            throw error;
        }
    }
    logoutAndClearAll() {
        this.clearSwitched();
        this.clearLoginUser();
        this.clearMenu();
        this.logout();
    }
    logout() {
        try {
            localStorage.removeItem('entraAccessToken');
            const activeAccount = this.msal.instance.getActiveAccount();
            if (activeAccount) {
                this.msal.instance.setActiveAccount(null);
                //利用靜默獲取token的方式來確認用戶是否還有效登錄狀態
                this.msal.instance.acquireTokenSilent({
                    account: activeAccount,
                    scopes: this.envServ.authConfig.entraID.scopes,
                    forceRefresh: true
                }).then(async res => {
                    if (res?.accessToken) {
                        //強制清除前一次未處理完的重定向，避免登出異常
                        await this.msal.handleRedirectObservable().toPromise().catch(err => { console.error('Redirect error:', err); });
                        //用戶仍然有效登錄，調用logoutRedirect登出
                        this.msal.logoutRedirect({
                            account: activeAccount,
                            postLogoutRedirectUri: '/login',
                        });
                    } else {
                        //用戶無效，直接跳轉登錄頁
                        window.location.href = '/login';
                    }
                }).catch(() => {
                    window.location.href = '/login';
                });
            } else {
                window.location.href = '/login';
            }
        }
        catch (error) {
            console.error('Error during logout:', error);
            window.location.href = '/login';
        }
    }
    refreshToken(): Promise<string> {
        if (sessionStorage.getItem('RefreshTokenTryCount') === '1') {
            sessionStorage.setItem('RefreshTokenTryCount', '2');
        } else {
            sessionStorage.setItem('RefreshTokenTryCount', '1');
        }

        let isForceRefresh = false;
        //取當前token,如果剩餘小於3分鐘就強制刷新
        const currentToken = localStorage.getItem('entraAccessToken');
        if (currentToken) {
            const payload = jwtDecode<{ exp?: number }>(currentToken);
            const exp = payload.exp;
            const currentTimeSec = Math.floor(Date.now() / 1000);
            if (exp - currentTimeSec < 180) {
                isForceRefresh = true;
                console.log('AD Token is about to expire, forcing refresh.');
            }
        }
        //msal內部有緩存機制，所以不用擔心執行頻次/效能的問題
        const account = this.getAccount();
        return this.msal.instance.acquireTokenSilent({
            account: account,
            scopes: this.envServ.authConfig.entraID.scopes,
            forceRefresh: isForceRefresh
        }).then(response => {
            sessionStorage.removeItem('RefreshTokenTryCount');
            localStorage.setItem('entraAccessToken', response.accessToken);
            console.log('AD Token refreshed successfully at ' + new Date().toLocaleString());
            const payload = jwtDecode<{ exp?: number }>(response.accessToken);
            const exp = payload.exp;
            console.log('AD Token expiration at ' + new Date((exp || 0) * 1000).toLocaleString());
            return response.accessToken;
        }).catch(error => {
            if (sessionStorage.getItem('RefreshTokenTryCount') === '1') {
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(this.refreshToken());
                    }, 2000); // 等待 2 秒 (2000 毫秒)
                });
            } else {
                //失敗回登錄頁
                sessionStorage.removeItem('RefreshTokenTryCount');
                console.error('Error refreshing token:', error);
                window.location.href = '/login';
                throw error;
            }
        });
    }
    getAccount(): AccountInfo | null {
        const activeAccount = this.msal.instance.getActiveAccount();
        if (activeAccount) {
            return activeAccount;
        }

        const accounts = this.msal.instance.getAllAccounts();
        if (accounts.length === 1) {
            this.msal.instance.setActiveAccount(accounts[0]);
            return accounts[0];
        }

        return null;
    }
    async isLoggedIn(): Promise<boolean> {
        // const accessToken = localStorage.getItem('entraAccessToken');
        // if (accessToken) {
        //     //token是否过期
        //     try {
        //         const payload = jwtDecode<{ exp?: number }>(accessToken);
        //         const exp = payload.exp;
        //         if (exp !== undefined) {
        //             const currentTimeSec = Math.floor(Date.now() / 1000);
        //             if (exp > currentTimeSec) {
        //                 return true;
        //             }
        //         } else {
        //             console.error('無法獲取 exp 字段');
        //         }
        //     }
        //     catch (error) {
        //         console.error('無法解析:', error);
        //     }
        // }
        // return false;
        //Hiker2026/03/05 用戶關閉瀏覽器超1小時，再回來，雖然token已過期，但其實msal的session仍然有效，這邊改成只要msal session有效就認為用戶是登錄狀態
        //過期的token會在layout init時自動刷新，這樣用戶體驗會更好，不會因為token過期而被迫登出
        //停在頁面閒置30分鐘，有另外做強制登出
        const account = this.getAccount();
        if (!account) {
            return false;
        }
        try {
            //嘗試靜默獲取token來確認session&用戶是否真的有效
            const response = await this.msal.instance.acquireTokenSilent({
                account: account,
                scopes: this.envServ.authConfig.entraID.scopes
            });
            if (response && response.accessToken) {
                await this.refreshToken(); //執行一下refresh把token更新到localstorage
                return true;
            }
            else {
                return false;
            }
        }
        catch (err) {
            console.error('Silent token acquisition failed, user may need to log in again.', err);
            return false;
        }
    }
    getToken() {
        try {
            const switchedUserToken = this.getSwitchedToken();
            if (switchedUserToken) {
                //檢查是否過期
                const exp = this.getSwitchedTokenExpiration();
                if (exp && this.calculateSwitchedTokenRemaining(exp) > 0) {
                    return switchedUserToken;
                } else {
                    this.clearSwitched();
                }
            }

            const entraToken = localStorage.getItem('entraAccessToken');
            if (entraToken) {
                return entraToken;
            }
            return '';

        } catch (error) {
            console.error('Error acquiring token:', error);
            return '';
        }
    }

    getSwitchedTokenExpiration(): number | null {
        const token = this.getSwitchedToken();
        if (!token) return null;
        // 解析 JWT token，获取 exp 字段
        const decoded = jwtDecode<{ exp?: number }>(token);
        return decoded.exp!;
    }
    calculateSwitchedTokenRemaining(exp: number): number {
        const now = Math.floor(Date.now() / 1000); // 当前 UNIX 时间戳（秒）
        return Math.max(exp - now, 0);
    }

    handleMenus(): Observable<{ displayMenus: JsMenu[], authMenus: JsMenu[] }> {
        const displayMenus$ = this.getTreeMenu().pipe(
            map(res => {
                if (res.rc !== 0) {
                    localStorage.setItem('DisplayMenus', '');
                    throw new Error('Failed to fetch display menu data');
                } else {
                    const menus = res.data || [];
                    // 反轉 isExternalMenu 狀態 => 因為 isExternalMenu 是以新版 MVM 的視角而言，這裡是舊版 MVM，所以要反轉
                    menus.forEach(menu => {
                        this.reverseIsExternalMenu(menu);
                    });
                    localStorage.setItem('DisplayMenus', JSON.stringify(menus));
                    return menus;
                }
            })
        );

        const authMenus$ = this.getAllAllowMenu().pipe(
            map(res => {
                if (res.rc !== 0) {
                    localStorage.setItem('AuthMenus', '');
                    throw new Error('Failed to fetch auth menu data');
                } else {
                    const menus = res.data || [];
                    // 反轉 isExternalMenu 狀態 => 因為 isExternalMenu 是以新版 MVM 的視角而言，這裡是舊版 MVM，所以要反轉
                    menus.forEach(menu => {
                        this.reverseIsExternalMenu(menu);
                    });

                    localStorage.setItem('AuthMenus', JSON.stringify(menus));
                    return menus;
                }
            })
        );

        return forkJoin({
            displayMenus: displayMenus$,
            authMenus: authMenus$
        });
    }

    private reverseIsExternalMenu(menu: JsMenu) {
        if (menu.isExternalMenu === 'Y') {
            menu.isExternalMenu = 'N';
        } else if (menu.isExternalMenu === 'N') {
            menu.isExternalMenu = 'Y';
        }
        if (menu.childMenus && menu.childMenus.length > 0) {
            menu.childMenus.forEach(child => {
                this.reverseIsExternalMenu(child);
            });
        }
    }

    private getTreeMenu() {
        const url = `${this.envServ.authConfig.MVMBackEnd}${URLConst.GetTreeMenu.url}`;
        return this.doGet(url);
    }

    private getAllAllowMenu() {
        const url = `${this.envServ.authConfig.MVMBackEnd}${URLConst.GetAllAllowMenu.url}`;
        return this.doGet(url);
    }

    private doGet(url: string): Observable<any> {
        const lang = this.getLang();
        url += (url.indexOf('?') > 0 ? '&' : '?') + 'q=' + new Date().getTime();
        const headers = new HttpHeaders().set('Authorization', 'Bearer ' + this.getToken())
            .set('IdentityType', this.getCurrentUserInfo()?.userType || '')
            .set('Accept-Language', lang);
        return this.httpClient.get(url, { headers });
    }

    private getLang(): string {
        return sessionStorage.getItem('wmsmlang')
            || localStorage.getItem('wmsmlang')
            || 'zh-TW';
    }

    clearMenu() {
        localStorage.removeItem('DisplayMenus');
        localStorage.removeItem('AuthMenus');
    }
}





